import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import moment from 'moment'
import Modal from '../../../components/ui/modal'
import { Button } from '../../../components/ui/button'
import { Calendar, ErrorIcon, Success } from '../../../assets/icons'
import theme from '../../../styles/theme'
import useIsMobile from '../../../hooks/useIsMobile'
import { capitalizeEachWord } from '../../../utils/stringsUtils'
import { buildAppointmentCalendarEvent } from '../../../utils/appointmentCalendarEvent'
import AddToCalendarQuickAccessModal from '../../../components/addToCalendarQuickAccessModal'
import {
  Container,
  HeaderSection,
  CardsGrid,
  Card,
  CardInfo,
  ButtonWrapper,
  Badge,
  LinkButton
} from './styled'
import Feedback from '../../../components/feedback/Feedback'

const MultipleAppointmentsSuccess = ({ cartTurns, clinics, result, onRetry, onBackInit }) => {
  const history = useHistory()
  const isMobile = useIsMobile()
  const [calendarEvent, setCalendarEvent] = useState(null)
  const [selectedErrorMessage, setSelectedErrorMessage] = useState(null)

  const turns = cartTurns || []
  const turnResults = result?.turns || []
  const getTurnResult = turn => turnResults.find(r => r.idTurno === turn.idTurno)
  const isTurnError = turn => !!getTurnResult(turn)?.hasError
  const getTurnErrorMessage = turn => getTurnResult(turn)?.errorMessage || 'Error al agendar el turno'
  const hasErrors = useMemo(() => turnResults.some(r => r.hasError), [turnResults])

  const getClinicName = turn =>
    clinics?.find(clinic => String(clinic.value) === String(turn.clinica))?.label

  	const getClinicDireccion = turn => turn.direccion

  const formatDate = turn => {
    const date = moment(turn.fechaTurnoDateTime)
    return date.isValid() ? date.format('DD/MM/YYYY') : ''
  }

  const formatTime = turn => {
    const date = moment(turn.fechaTurnoDateTime)
    return date.isValid() ? date.format('HH:mm') : ''
  }

  return (
    <>
      <AddToCalendarQuickAccessModal
        isOpen={!!calendarEvent}
        onClose={() => setCalendarEvent(null)}
        isFullHeight={isMobile}
        event={calendarEvent}
      />

      {selectedErrorMessage && (
        <Modal onClose={() => setSelectedErrorMessage(null)} isFullHeight={isMobile} widthDesktop="440px">
					<Feedback
						title="Error al agendar el turno"
						//por ahora no muestra mensaje del back
						// bodyText={selectedErrorMessage}
						bodyText="Ocurrió un error al agendar el turno"
						buttonLabel="Cerrar"
						onClickButton={() => setSelectedErrorMessage(null)}
						isSucces={false}
						isError={true}
						icon={<ErrorIcon color={theme.colors.feedback.error} />}
						isModal={true}
						pt={80}
						widthDesktop="440px"
					/>
        </Modal>
      )}

      <Container>
        <HeaderSection>
          {hasErrors ? (
            <ErrorIcon size={64} color={theme.colors.feedback.error} />
          ) : (
            <Success size={64} color={theme.colors.brand.green} />
          )}
          <h1
            style={{
              fontSize: isMobile ? '22px' : '28px',
              color: theme.colors.text.primaryText,
              textAlign: 'center',
              margin: '16px 0 8px 0'
            }}
          >
            {hasErrors
              ? 'No se pudieron agendar todos los turnos'
              : '¡Tus turnos fueron agendados con éxito!'}
          </h1>
          <p style={{ color: theme.colors.text.secondaryText, textAlign: 'center', margin: 0 }}>
            {hasErrors
              ? 'Revisá el detalle e intentá nuevamente con los turnos que presentaron errores.'
              : 'Podrás ver los detalles en la pantalla de inicio.'}
          </p>
        </HeaderSection>

        <CardsGrid>
          {turns?.map((turn, index) => (
            <Card key={turn.idTurno || index}>
              {isTurnError(turn) ? (
                <Badge $variant="error">
                  <ErrorIcon size={14} color={theme.colors.feedback.error} />
                  <span>Error al agendar</span>
                </Badge>
              ) : (
                <Badge $variant="success">Agendado</Badge>
              )}
              <CardInfo>
                <p style={{ fontSize: '16px', fontWeight: 700, color: theme.colors.text.primaryText, margin: 0 }}>
                  {capitalizeEachWord(turn.nombreProfesional || '')}
                </p>
                <p style={{ fontSize: '14px', color: theme.colors.text.secondaryText, margin: 0 }}>
                  {[capitalizeEachWord(turn.nombreEspecialidad || ''), formatDate(turn), formatTime(turn)]
                    .filter(Boolean)
                    .join(' • ')}
                </p>
                <p style={{ fontSize: '14px', color: theme.colors.text.secondaryText, margin: 0 }}>
                  {capitalizeEachWord(getClinicName(turn) || '')}
                </p>
                <p style={{ fontSize: '14px', color: theme.colors.text.secondaryText, margin: 0 }}>
                  {capitalizeEachWord(getClinicDireccion(turn) || '')}
                </p>
              </CardInfo>
              {isTurnError(turn) ? (
                <LinkButton onClick={() => setSelectedErrorMessage(getTurnErrorMessage(turn))}>
                  Ver detalle
                </LinkButton>
              ) : (
                <LinkButton onClick={() => setCalendarEvent(buildAppointmentCalendarEvent(turn, { clinicName: getClinicName(turn) }))}>
                  <Calendar size={16} color={theme.colors.brand.blueOmint} />
                  <span>Agregar a mi calendario</span>
                </LinkButton>
              )}
            </Card>
          ))}
        </CardsGrid>

        <ButtonWrapper>
          {hasErrors ? (
            <>
              <Button
                label="Volver a intentar"
                $variant="primary"
                size={isMobile ? 'mobile' : 'xlarge'}
                onClick={() => onRetry && onRetry()}
              />
              <LinkButton style={{ alignSelf: 'center', marginTop: '16px' }} onClick={() => history.push('/')}>
                Volver al inicio
              </LinkButton>
            </>
          ) : (
            <Button
              label="Volver al inicio"
              $variant="primary"
              size={isMobile ? 'mobile' : 'xlarge'}
              onClick={() => onBackInit ? onBackInit() : history.push('/')}
            />
          )}
        </ButtonWrapper>
      </Container>
    </>
  )
}

function mapStateToProps({ appointment }) {
  return {
    cartTurns: appointment.booking?.cartTurns,
    clinics: appointment.booking?.catalogs?.clinics,
    result: appointment.booking?.result
  }
}

export default connect(mapStateToProps)(MultipleAppointmentsSuccess)
